<template>
  <div>
    <!-- delete confirm -->
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <form @submit.prevent="onSubmit">
      <!-- TOP ROW (HEADER) -->
      <div class="row h-40">
        <p class="bb-page-title mt-3">COMBATANT</p>
      </div>
      <!-- MIDDLE ROW -->
      <div class="row">
        <!-- LEFT COL -->
        <div class="col-3 m-2">
          <div class="row">
            <!-- IMAGE -->
            <div class="form-group text-center">
              <div class="form-floating mb-2">
                <img
                  v-bind:src="image"
                  height="200"
                  width="350"
                  class="img-fluid rounded border border-dark bb-image-container"
                  alt="combatant image"
                />
              </div>
              <label class="btn btn-dark btn-file mb-2 bb-click-label">
                Change Image
                <input
                  type="file"
                  accept="image/*"
                  style="display: none"
                  id="selectImage"
                />
              </label>
            </div>
          </div>
          <!-- BUTTONS -->
          <div class="row mt-4">
            <div
              class="btn-group-vertical btn-group-lg"
              role="group"
            >
              <!-- ------------ -->
              <!--    cancel    -->
              <!-- ------------ -->
              <button
                class="btn btn-lg btn-warning"
                type="button"
                @click="closeSidePanel()"
              >
                Cancel
              </button>
              <!-- ------------ -->
              <!--     add      -->
              <!-- ------------ -->
              <button
                v-if="new_combatant"
                class="btn btn-primary btn-lg"
                type="submit"
              >
                Add
              </button>
              <!-- ------------ -->
              <!--      ok      -->
              <!-- ------------ -->
              <button
                v-if="!new_combatant"
                :disabled="!isChanged"
                class="btn btn-primary btn-lg"
                type="submit"
              >
                OK
              </button>
              <!-- ------------ -->
              <!--     prev     -->
              <!-- ------------ -->
              <button
                v-if="combatant != null && combatantNames.length > 1"
                type="button"
                class="btn btn-primary btn-lg"
                @click="prevCombatant()"
              >
                Previous
              </button>
              <!-- ------------ -->
              <!--     next     -->
              <!-- ------------ -->
              <button
                v-if="combatant != null && combatantNames.length > 1"
                type="button"
                class="btn btn-primary btn-lg"
                @click="nextCombatant()"
              >
                Next
              </button>
              <!-- ----------- -->
              <!--    delete   -->
              <!-- ----------- -->
              <button
                v-if="combatant && !this.delete"
                class="btn btn-primary btn-lg"
                type="button"
                @click="confirmDeletion()"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
        <!-- RIGHT COL (DATA) -->
        <div class="col ml-2">
          <!------------------------------->
          <div class="row mb-3">
            <!-- -------------- -->
            <!--      qty       -->
            <!-- -------------- -->
            <div
              v-if="local_combatant.type == 'Troop' && combatant == null"
              class="col-5 me-1"
            >
              <CounterComponent
                :pass_ref="'counter_qty'"
                :pass_label="qty_label"
                :pass_count="qty"
                :pass_min="qty_min"
                :pass_max="qty_max"
                :pass_digits="qty_digits"
              />
            </div>
            <!-- -------------- -->
            <!--   seq (r/o)    -->
            <!-- -------------- -->
            <div
              v-if="local_combatant.status == 'Active'"
              class="col-3"
            >
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    v-model="seq"
                    id="seq"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="seq"
                    >Seq</label
                  >
                </div>
              </div>
            </div>
            <!-- -------------- -->
            <!--   rank (r/o)   -->
            <!-- -------------- -->
            <div
              v-if="local_combatant.status == 'Active'"
              class="col-3 me-4"
            >
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    v-model="rank"
                    id="rank"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="rank"
                    >Rank</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!------------------------------->
          <div class="row mb-2">
            <!-- -------------- -->
            <!--      name      -->
            <!-- -------------- -->
            <div class="col-5">
              <div class="form-group">
                <div class="form-floating">
                  <!-- -------------- -->
                  <!--   character    -->
                  <!-- -------------- -->
                  <input
                    v-if="isCharacter"
                    readonly
                    class="form-control capitalize"
                    v-model="local_combatant.name"
                    id="name"
                    disabled
                  />
                  <!-- -------------- -->
                  <!-- not character  -->
                  <!-- -------------- -->
                  <input
                    v-if="!isCharacter"
                    ref="input"
                    v-model="local_combatant.name"
                    v-bind:class="{
                      'is-invalid': errorFields.includes('name'),
                    }"
                    id="name"
                    name="name"
                    type="search"
                    placeholder="..."
                    class="form-control capitalize"
                    @keydown="onKeyDown($event)"
                    @input="clearInputError('name')"
                    @blur="validateName()"
                    @click="clearInputError('name')"
                    @mouseup="this.capitaliseHandler(local_combatant.name)"
                    autocomplete="off"
                    required
                  />
                  <label
                    :class="['bb-float-label', { optional: isCharacter }]"
                    for="name"
                    >Name</label
                  >
                </div>
              </div>
            </div>
            <!-- -------------- -->
            <!--      role      -->
            <!-- -------------- -->
            <div
              v-if="local_combatant.type == 'Troop'"
              class="col"
            >
              <div
                class="form-group bb-role-preview-anchor"
                @mouseenter="showRolePreview(local_combatant.role)"
                @mouseleave="hideRolePreview"
              >
                <div class="form-floating">
                  <!-- -------------- -->
                  <!--   character  -->
                  <!-- -------------- -->
                  <input
                    v-if="isCharacter"
                    readonly
                    class="form-control"
                    v-model="local_combatant.role"
                    id="role"
                    disabled
                    @mouseenter="showRolePreview(local_combatant.role)"
                  />
                  <!-- -------------- -->
                  <!-- not character  -->
                  <!-- -------------- -->
                  <v-select
                    v-if="!isCharacter"
                    :options="role_labels"
                    class="form-control"
                    placeholder="select..."
                    v-model="local_combatant.role"
                    @keydown="onKeyDown($event)"
                    @open="showRolePreview(local_combatant.role)"
                    required
                    id="role"
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_combatant.role"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                    <template #selected-option="roleOption">
                      <span
                        class="bb-role-select-value"
                        @mouseenter="showRolePreview(roleOption)"
                      >
                        {{ formatRoleOption(roleOption) }}
                      </span>
                    </template>
                    <template #option="roleOption">
                      <div
                        class="bb-role-option"
                        @mouseenter="showRolePreview(roleOption)"
                      >
                        {{ formatRoleOption(roleOption) }}
                      </div>
                    </template>
                  </v-select>
                  <label
                    :class="['bb-float-label', { optional: isCharacter }]"
                    for="role"
                    >Role</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!------------------------------->
          <div class="row mb-2">
            <!-- -------------- -->
            <!--    category    -->
            <!-- -------------- -->
            <div class="col-5">
              <div class="form-group">
                <div class="form-floating">
                  <!-- -------------- -->
                  <!--   character    -->
                  <!-- -------------- -->
                  <input
                    v-if="isCharacter"
                    readonly
                    class="form-control"
                    v-model="local_combatant.category"
                    id="category"
                    disabled
                  />
                  <!-- -------------- -->
                  <!-- not character  -->
                  <!-- -------------- -->
                  <v-select
                    v-if="!isCharacter"
                    :options="categories"
                    class="form-control"
                    placeholder="select..."
                    v-model="local_combatant.category"
                    @keydown="onKeyDown($event)"
                    id="category"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_combatant.category"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    :class="['bb-float-label', { optional: isCharacter }]"
                    for="category"
                    >Category</label
                  >
                </div>
              </div>
            </div>
            <!-- -------------- -->
            <!--      type      -->
            <!-- -------------- -->
            <div class="col">
              <div class="form-group">
                <div class="form-floating">
                  <!-- -------------- -->
                  <!--   character    -->
                  <!-- -------------- -->
                  <input
                    v-if="isCharacter"
                    readonly
                    class="form-control"
                    v-model="local_combatant.type"
                    id="type"
                    disabled
                  />
                  <!-- -------------- -->
                  <!-- not character  -->
                  <!-- -------------- -->
                  <v-select
                    v-if="!isCharacter"
                    :options="types"
                    class="form-control"
                    placeholder="select..."
                    v-model="local_combatant.type"
                    @keydown="onKeyDown($event)"
                    id="type"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_combatant.type"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    :class="['bb-float-label', { optional: isCharacter }]"
                    for="type"
                    >Type</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!------------------------------->
          <div class="row mb-2">
            <!-- -------------- -->
            <!--      race      -->
            <!-- -------------- -->
            <div class="col-5">
              <div class="form-group">
                <div class="form-floating">
                  <!-- -------------- -->
                  <!--   character    -->
                  <!-- -------------- -->
                  <input
                    v-if="isCharacter"
                    readonly
                    class="form-control"
                    v-model="local_combatant.race"
                    id="race"
                    disabled
                  />
                  <!-- -------------- -->
                  <!-- not character  -->
                  <!-- -------------- -->
                  <v-select
                    v-if="!isCharacter"
                    :options="race_names"
                    class="form-control"
                    placeholder="select..."
                    v-model="local_combatant.race"
                    @keydown="onKeyDown($event)"
                    id="race"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_combatant.race"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    :class="['bb-float-label', { optional: isCharacter }]"
                    for="race"
                    >Race</label
                  >
                </div>
              </div>
            </div>
            <!-- -------------- -->
            <!--     player     -->
            <!-- -------------- -->
            <div
              v-if="local_combatant.type != 'Monster'"
              class="col"
            >
              <div class="form-group">
                <div class="form-floating">
                  <v-select
                    :options="player_names"
                    class="form-control"
                    v-model="local_combatant.player"
                    @keydown="onKeyDown($event)"
                    id="player"
                  >
                  </v-select>
                  <label
                    class="bb-float-label optional"
                    for="player"
                    >Player</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!------------------------------->
          <div class="row mb-2">
            <div class="col-5">
              <div class="form-group">
                <div class="form-floating">
                  <v-select
                    :options="visibility_options"
                    :reduce="(option) => option.value"
                    class="form-control bb-visibility-select"
                    v-model="local_combatant.visibility"
                    @keydown="onKeyDown($event)"
                    id="visibility"
                  >
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="visibility"
                    >Visibility</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!---------------- ERRORS ------------------>
          <div
            v-if="errors.length > 0"
            class="row mt-3 mb-2"
          >
            <div class="form-group">
              <!-- ------------ -->
              <!--    errors    -->
              <!-- ------------ -->
              <div
                v-for="err in errors"
                :key="err.id"
                class="bb-error"
              >
                {{ err }}
              </div>
            </div>
          </div>
          <!-- col 2/row 3 -->
          <!----------------LEFT COL ------------------>
          <div class="row mt-2">
            <div class="col-5">
              <!------------- ABILITIES --------------->
              <!-- -------------- -->
              <!--      chars     -->
              <!-- -------------- -->
              <p class="bb-sub-hdg mt-2">Abilities</p>
              <!-- -------------- -->
              <!--      CA        -->
              <!-- -------------- -->
              <div class="row mb-2 w-75">
                <CounterComponent
                  :pass_ref="'counter_ca'"
                  :pass_label="ca_label"
                  :pass_count="local_combatant.ca"
                  :pass_min="ca_min"
                  :pass_max="ca_max"
                  :pass_digits="ca_digits"
                />
              </div>
              <!-- -------------- -->
              <!--      CR        -->
              <!-- -------------- -->
              <div class="row mb-2 w-75">
                <CounterComponent
                  :pass_ref="'counter_cr'"
                  :pass_label="cr_label"
                  :pass_count="local_combatant.cr"
                  :pass_min="cr_min"
                  :pass_max="cr_max"
                  :pass_digits="cr_digits"
                />
              </div>
              <!-- -------------- -->
              <!--      dex       -->
              <!-- -------------- -->
              <div class="row mb-2 w-75">
                <CounterComponent
                  :pass_ref="'counter_dex'"
                  :pass_label="dex_label"
                  :pass_count="local_combatant.dex"
                  :pass_min="char_min"
                  :pass_max="char_max"
                  :pass_digits="dex_digits"
                />
              </div>
              <!-- --------------- -->
              <!--    damage mod   -->
              <!-- --------------- -->
              <div class="row mb-2 w-75">
                <div class="form-group">
                  <div class="form-floating">
                    <v-select
                      :options="damage_mods"
                      class="form-control"
                      placeholder="select..."
                      v-model="local_combatant.damage_mod"
                      id="damage_mod"
                    >
                      <template v-slot:selected-option="option">
                        <span class="bb-center-text">
                          {{ option.label }}
                        </span>
                      </template>
                    </v-select>
                    <label
                      class="bb-float-label"
                      for="damage_mod"
                      >Dam Mod</label
                    >
                  </div>
                </div>
              </div>
            </div>
            <!---------------- RIGHT COL ------------------>
            <div class="col-7 mt-2">
              <!---------------- OPTIONS ------------------>
              <!-- -------------- -->
              <!--     options    -->
              <!-- -------------- -->
              <p class="bb-sub-hdg">Options</p>
              <div class="col px-3 py-2">
                <!-- -------------- -->
                <!--   2nd Weapon   -->
                <!-- -------------- -->
                <div class="row">
                  <enhanced-toggle
                    :key="`second-weapon-${local_combatant.id}-${local_combatant.second_weapon}`"
                    style="
                      --toggleWidth: 380px;
                      --labelWidth: 240px;
                      --switchWidth: 60px;
                      --switchPos: 275px;
                      height: 50px;
                      border-radius: 10px;
                      background-color: white;
                    "
                    :styleOff="`default`"
                    :styleOn="`primary`"
                    :labelOn="`2nd Weapon`"
                    :labelOff="`Single Weapon`"
                    :checked="local_combatant.second_weapon"
                    @mouseup="onToggleClick('second_weapon')"
                    @click.stop="onToggleClick('second_weapon')"
                  >
                  </enhanced-toggle>
                </div>
                <!-- -------------- -->
                <!--    shield      -->
                <!-- -------------- -->
                <div class="row">
                  <enhanced-toggle
                    :key="`shield-${local_combatant.id}-${local_combatant.shield}`"
                    style="
                      --toggleWidth: 380px;
                      --labelWidth: 240px;
                      --switchWidth: 60px;
                      --switchPos: 275px;
                      height: 50px;
                      border-radius: 10px;
                      background-color: white;
                    "
                    :labelOn="`Shield`"
                    :labelOff="`No Shield`"
                    :styleOff="`default`"
                    :styleOn="`primary`"
                    :checked="local_combatant.shield"
                    @mouseup="onToggleClick('shield')"
                    @click.stop="onToggleClick('shield')"
                  >
                  </enhanced-toggle>
                </div>
                <!-- -------------- -->
                <!--    ranged    -->
                <!-- -------------- -->
                <div class="row">
                  <enhanced-toggle
                    :key="`ranged-${local_combatant.id}-${local_combatant.ranged}`"
                    style="
                      --toggleWidth: 380px;
                      --labelWidth: 240px;
                      --switchWidth: 60px;
                      --switchPos: 275px;
                      height: 50px;
                      border-radius: 10px;
                      background-color: white;
                    "
                    :labelOn="`Ranged`"
                    :labelOff="`No Ranged`"
                    :styleOff="`default`"
                    :styleOn="`primary`"
                    :checked="local_combatant.ranged"
                    @mouseup="onToggleClick('ranged')"
                    @click.stop="onToggleClick('ranged')"
                  >
                  </enhanced-toggle>
                </div>
                <!-- -------------- -->
                <!--   magic-user   -->
                <!-- -------------- -->
                <div class="row">
                  <enhanced-toggle
                    :key="`magic-${local_combatant.id}-${local_combatant.magic}`"
                    style="
                      --toggleWidth: 380px;
                      --labelWidth: 240px;
                      --switchWidth: 60px;
                      --switchPos: 275px;
                      height: 50px;
                      border-radius: 10px;
                      background-color: white;
                    "
                    :labelOn="`Magic`"
                    :labelOff="`No Magic`"
                    :styleOff="`default`"
                    :styleOn="`primary`"
                    :checked="local_combatant.magic"
                    @mouseup="onToggleClick('magic')"
                    @click.stop="onToggleClick('magic')"
                  >
                  </enhanced-toggle>
                </div>
                <!-- ---------------- -->
                <!--  special action  -->
                <!-- ---------------- -->
                <div class="row">
                  <enhanced-toggle
                    :key="`special-action-${local_combatant.id}-${local_combatant.special_action}`"
                    style="
                      --toggleWidth: 380px;
                      --labelWidth: 240px;
                      --switchWidth: 60px;
                      --switchPos: 275px;
                      height: 50px;
                      border-radius: 10px;
                      background-color: white;
                    "
                    :labelOn="`Special Action`"
                    :labelOff="`Normal Actions`"
                    :styleOff="`default`"
                    :styleOn="`primary`"
                    :checked="local_combatant.special_action"
                    @mouseup="onToggleClick('special_action')"
                    @click.stop="onToggleClick('special_action')"
                  >
                  </enhanced-toggle>
                </div>
                <!-- ----------------- -->
                <!-- special reaction  -->
                <!-- -----------------   -->
                <div class="row">
                  <enhanced-toggle
                    :key="`special-reaction-${local_combatant.id}-${local_combatant.special_reaction}`"
                    style="
                      --toggleWidth: 380px;
                      --labelWidth: 240px;
                      --switchWidth: 60px;
                      --switchPos: 275px;
                      height: 50px;
                      border-radius: 10px;
                      background-color: white;
                    "
                    :labelOn="`Special Reaction`"
                    :labelOff="`Normal Reactions`"
                    :styleOff="`default`"
                    :styleOn="`primary`"
                    :checked="local_combatant.special_reaction"
                    @mouseup="onToggleClick('special_reaction')"
                    @click.stop="onToggleClick('special_reaction')"
                  >
                  </enhanced-toggle>
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-4">
            <!------------- STRIKE RANKS  --------------->
            <div class="row mt-2">
              <p class="bb-sub-hdg">Strike Rank</p>
            </div>
            <!-- ----------------------- -->
            <!--   strike ranks row  1   -->
            <!-- ----------------------- -->
            <div class="row">
              <!-- -------------- -->
              <!--      SR        -->
              <!-- -------------- -->
              <div class="col-sm-4">
                <CounterComponent
                  :pass_ref="'counter_sr'"
                  :pass_label="sr_label"
                  :pass_count="local_combatant.sr"
                  :pass_min="sr_min"
                  :pass_max="sr_max"
                  :pass_digits="sr_digits"
                />
              </div>
              <!-- -------------- -->
              <!--   rolled SR    -->
              <!-- -------------- -->
              <div class="col-sm-4">
                <CounterComponent
                  :pass_ref="'counter_sr_roll'"
                  :pass_label="sr_roll_label"
                  :pass_count="local_combatant.sr_roll"
                  :pass_min="sr_roll_min"
                  :pass_max="sr_roll_max"
                  :pass_digits="sr_roll_digits"
                />
              </div>
            </div>
            <!-- ----------------------- -->
            <!--   strike ranks row  2   -->
            <!-- ----------------------- -->
            <div class="row mt-3">
              <!-- -------------- -->
              <!--     SR adj     -->
              <!-- -------------- -->
              <div class="col-sm-4">
                <CounterComponent
                  :pass_ref="'counter_sr_adj'"
                  :pass_label="sr_adj_label"
                  :pass_count="local_combatant.sr_adj"
                  :pass_min="sr_adj_min"
                  :pass_max="sr_adj_max"
                  :pass_digits="sr_adj_digits"
                />
              </div>
              <!-- -------------- -->
              <!--   roll off     -->
              <!-- -------------- -->
              <div class="col-sm-4">
                <CounterComponent
                  :pass_ref="'counter_roll_off'"
                  :pass_label="roll_off_label"
                  :pass_count="local_combatant.roll_off"
                  :pass_min="roll_off_min"
                  :pass_max="roll_off_max"
                  :pass_digits="roll_off_digits"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
    <teleport to="body">
      <div
        v-if="rolePreviewVisible && rolePreviewDetails"
        class="bb-role-preview-panel"
      >
        <table class="table table-sm mb-0 bb-role-preview-table">
          <thead>
            <tr>
              <th class="bb-role-preview-heading">Image</th>
              <th class="bb-role-preview-heading">Name</th>
              <th class="bb-role-preview-heading">SR Adj</th>
              <th class="bb-role-preview-heading">Dex Adj</th>
              <th class="bb-role-preview-heading">CA Adj</th>
              <th class="bb-role-preview-heading">Options</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="bb-role-preview-image-cell">
                <img
                  :src="rolePreviewDetails.image || image"
                  class="bb-role-preview-image rounded"
                  alt="role preview image"
                />
              </td>
              <td class="bb-role-preview-value">{{ rolePreviewName }}</td>
              <td class="bb-role-preview-value">
                {{ formatSignedValue(rolePreviewDetails.sr_adj) }}
              </td>
              <td class="bb-role-preview-value">
                {{ formatSignedValue(rolePreviewDetails.dex_adj) }}
              </td>
              <td class="bb-role-preview-value">
                {{ formatSignedValue(rolePreviewDetails.ca_adj) }}
              </td>
              <td class="bb-role-preview-value">
                <div class="bb-role-preview-options">
                  <img
                    v-for="optionName in getActiveRoleOptionNames(
                      rolePreviewDetails,
                    )"
                    :key="optionName"
                    :src="getOptionImageSrc(optionName)"
                    class="bb-role-preview-option rounded bb-img-norm-border"
                    :alt="`${optionName} option image`"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </teleport>
  </div>
</template>

<script>
import { EnhancedToggle } from 'vue-enhanced-check'

import CounterComponent from '../components/Counter.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'

import { mapGetters } from 'vuex'
import { inject } from 'vue'

import getStringAfterLastSlash from '../helpers/getStringAfterLastSlash'
import capitaliseMultipleWords from '../helpers/capitaliseMultipleWords'
import loadCombatantValues from '../helpers/loadCombatantValues'
import setCombatantValues from '../helpers/setCombatantValues'
import isObjectChanged from '../helpers/isObjectChanged'
import copyObjectNoRef from '../helpers/copyObjectNoRef'

import getRoleDetails from '../helpers/getRoleDetails'
import getRaceDetails from '../helpers/getRaceDetails'
import isAlphanumeric from '../helpers/isAlphanumeric'
import calcCombatAdj from '../helpers/calcCombatAdj'

import extractLabels from '../helpers/extractLabels'
import extractNames from '../helpers/extractNames'
import calcRank from '../helpers/calcRank'
import isName from '../helpers/isName'
import getOptionImage from '../helpers/getOptionImage'

import defaultImage from '../assets/images/default-combatant-image.png'

export default {
  name: 'CombatantCard',
  //========================================================
  // components
  //------------
  components: {
    EnhancedToggle,
    CounterComponent,
    ConfirmDialog,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // props
  //-------
  props: {
    combatant: {
      type: Object,
      default: null,
    },
    combatantNames: {
      type: Array,
      default: null,
    },
  },
  //========================================================
  // data
  //-------
  data() {
    return {
      defaultImage,
      delete: false,
      image_selected: false,
      new_combatant: false,
      submitCommitted: false,
      errors: [],
      errorFields: [],
      config: null,
      races: [],
      roles: [],
      damage_mods: [],
      original_name: '',
      original_race: '',
      original_role: '',
      original_combatant: null,
      isHydratingCombatant: false,
      defaultRaceImage: null,
      defaultRace: null,
      rolePreviewVisible: false,
      rolePreviewName: '',
      rolePreviewDetails: null,
      // local combatant
      local_combatant: {
        arena: null,
        id: 0,
        slug: '',
        seq: 0,
        name: '',
        player: '',
        race: '',
        category: '',
        type: '',
        role: '',
        status: '',
        sub_status: '',
        visibility: 'visible',
        damage_mod: '',
        ca: 0,
        cr: 0,
        dex: 0,
        sr: 0,
        sr_roll: 0,
        sr_adj: 0,
        roll_off: 0,
        second_weapon: false,
        magic: false,
        shield: false,
        ranged: false,
        special_action: false,
        special_reaction: false,
        image: null,
        image_file: null,
      },
      // drop-downs
      player_names: [],
      categories: [],
      types: [],
      race_names: [],
      role_labels: [],
      statuses: [],
      sub_status_lookup: [],
      visibility_options: [
        { label: 'Visible', value: 'visible' },
        { label: 'Concealed', value: 'concealed' },
        { label: 'Hidden', value: 'hidden' },
      ],
      // seq
      seq: 0,
      // qty
      qty: 1,
      qty_label: 'Qty',
      qty_min: 0,
      qty_max: 0,
      qty_digits: 2,
      // ca
      ca_label: 'CA',
      ca_min: 0,
      ca_max: 0,
      ca_digits: 1,
      // cr
      cr_label: 'CR',
      cr_min: 0,
      cr_max: 0,
      cr_digits: 1,
      // dex
      dex_label: 'Dex',
      dex_digits: 2,
      //
      char_min: 0,
      char_max: 0,
      char_default_val: 0,
      // sr
      sr_label: 'SR',
      sr_min: 0,
      sr_max: 0,
      sr_digits: 2,
      // sr_roll
      sr_roll_label: 'SR Roll',
      sr_roll_min: 0,
      sr_roll_max: 0,
      sr_roll_digits: 2,
      // sr_adj
      sr_adj_label: 'SR Adj',
      sr_adj_min: 0,
      sr_adj_max: 0,
      sr_adj_digits: 2,
      // roll_off
      roll_off_label: 'Roll-Off',
      roll_off_min: 0,
      roll_off_max: 0,
      roll_off_digits: 1,
    }
  },
  //========================================================
  // computed
  //----------
  computed: {
    //----------
    // has name
    //----------
    hasName() {
      if (
        this.local_combatant.name == '' ||
        this.local_combatant.name == undefined
      ) {
        return false
      } else {
        return true
      }
    },
    //------------
    // is changed
    //------------
    isChanged() {
      return isObjectChanged(this.original_combatant, this.local_combatant)
    },
    //--------------
    // is character
    //--------------
    isCharacter() {
      if (
        this.local_combatant.type == 'Character' ||
        this.local_combatant.type == 'NPC'
      ) {
        return true
      } else {
        return false
      }
    },
    //========================================================
    // watch variables
    //-----------------
    watch_race() {
      return this.local_combatant.race
    },
    watch_role() {
      return this.local_combatant.role
    },
    watch_shield() {
      return this.local_combatant.shield
    },
    watch_second_weapon() {
      return this.local_combatant.second_weapon
    },
    //========================================================
    // determine combatant image
    //---------------------------
    image: function () {
      if (this.local_combatant.image != null) {
        return this.local_combatant.image
      } else {
        return this.defaultImage
      }
    },
    //========================================================
    // setup substatus dropdown list for status
    //------------------------------------------
    sub_statuses() {
      if (this.sub_status_lookup != null) {
        const cur_status = this.local_combatant.status
        const ss_obj_array = this.sub_status_lookup.filter(function (el) {
          return el.status == cur_status
        })
        const ss_array = ss_obj_array.map(function (array) {
          return array.sub_status
        })
        return ss_array
      } else {
        return null
      }
    },
    //========================================================
    // calc rank
    //-----------
    rank() {
      return calcRank(this.local_combatant)
    },
    hideRank() {
      if (this.rank == 0) {
        return 'bb-hide-zero'
      } else {
        return null
      }
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    //========================================================
    // ref mapped methods
    //--------------------
    ...mapGetters('combat', {
      getConfig: 'getConfig',
      getRoles: 'getRoles',
      getPlayerNames: 'getPlayerNames',
    }),
    ...mapGetters('races', {
      getRaces: 'getRaces',
    }),
    //========================================================
    // capitalise handler
    //--------------------
    capitaliseHandler(str) {
      this.local_combatant.name = capitaliseMultipleWords(str)
    },
    //========================================================
    // on race change
    //----------------
    onRaceChange() {
      if (this.local_combatant.race != this.original_race) {
        // set new original name
        this.original_race = this.local_combatant.race
        // zeroise sr values
        this.setZeroSRValues()
        // get details for new race
        let raceDetails = getRaceDetails(this.local_combatant.race, this.races)
        // load new race details
        if (raceDetails) {
          this.local_combatant.image = raceDetails.image
          this.local_combatant.ca = raceDetails.ca
          this.local_combatant.cr = raceDetails.cr
          this.local_combatant.sr = raceDetails.sr
          ;(this.local_combatant.dex = raceDetails.characteristics.find(
            (c) => (c.name = 'DEX'),
          ).value),
            (this.local_combatant.damage_mod = raceDetails.damage_mod)
          //---------------
          // preload image
          //---------------
          this.preloadImage(this.local_combatant.image)
        }
        // check if apply toggles
        this.chkToggleClick('special_action', raceDetails.special_action)
        this.chkToggleClick('special_reaction', raceDetails.special_reaction)
      }
    },
    //========================================================
    // on role change
    //----------------
    onRoleChange() {
      if (this.local_combatant.role != this.original_role) {
        // get details for PREV role
        let prevRoleDetails = getRoleDetails(this.original_role, this.roles)

        // set new original name
        this.original_role = this.local_combatant.role

        // REVERSE ca adjs
        this.local_combatant.ca = calcCombatAdj({
          reverse: true,
          current_value: this.local_combatant.ca,
          min: this.config.min_ca,
          max: this.config.max_ca,
          adj: prevRoleDetails.ca_adj,
        })

        // REVERSE sr adjs
        this.local_combatant.sr = calcCombatAdj({
          reverse: true,
          current_value: this.local_combatant.sr,
          min: this.config.min_sr,
          max: this.config.max_sr,
          adj: prevRoleDetails.sr_adj,
        })

        // REVERSE dex adjs
        this.local_combatant.dex = calcCombatAdj({
          reverse: true,
          current_value: this.local_combatant.dex,
          min: this.config.min_dex,
          max: this.config.max_dex,
          adj: prevRoleDetails.dex_adj,
        })

        // get details for NEW role
        let newRoleDetails = getRoleDetails(
          this.local_combatant.role,
          this.roles,
        )

        // set role-based image if race = human
        if (this.local_combatant.race == 'Human') {
          //  + role not default
          if (this.local_combatant.role != 'Default') {
            this.local_combatant.image = newRoleDetails.image
          } else {
            this.local_combatant.image = this.defaultRaceImage
          }
          this.preloadImage(this.local_combatant.image)
        }

        // APPLY ca adjs
        this.local_combatant.ca = calcCombatAdj({
          reverse: false,
          current_value: this.local_combatant.ca,
          min: this.config.min_ca,
          max: this.config.max_ca,
          adj: newRoleDetails.ca_adj,
        })

        // APPLY sr adjs
        this.local_combatant.sr = calcCombatAdj({
          reverse: false,
          current_value: this.local_combatant.sr,
          min: this.config.min_sr,
          max: this.config.max_sr,
          adj: newRoleDetails.sr_adj,
        })

        // APPLY dex adjs
        this.local_combatant.dex = calcCombatAdj({
          reverse: false,
          current_value: this.local_combatant.dex,
          min: this.config.min_dex,
          max: this.config.max_dex,
          adj: newRoleDetails.dex_adj,
        })

        // check if APPLY toggles
        this.chkToggleClick('second_weapon', newRoleDetails.second_weapon)
        this.chkToggleClick('shield', newRoleDetails.shield)
        this.chkToggleClick('ranged', newRoleDetails.ranged)
        this.chkToggleClick('magic', newRoleDetails.magic)
        this.chkToggleClick('special_action', newRoleDetails.special_action)
        this.chkToggleClick('special_reaction', newRoleDetails.special_reaction)
      }
    },
    //========================================================
    // get dropdown values
    //---------------------
    getDropDownValues() {
      // get config
      this.config = this.getConfig()
      // load combatant categories
      this.categories = this.config.combatant_categories
      // load types
      this.types = this.config.roster_types
      // load statuses
      this.statuses = this.config.statuses
      // load sub-status lookup
      this.sub_status_lookup = this.config.sub_status_lookup
      // load counter min/max values
      this.sr_min = this.config.min_sr
      this.sr_max = this.config.max_sr
      this.ca_min = this.config.min_ca
      this.ca_max = this.config.max_ca
      this.cr_min = this.config.min_cr
      this.cr_max = this.config.max_cr
      this.sr_adj_min = this.config.min_sr_adj
      this.sr_adj_max = this.config.max_sr_adj
      this.roll_off_min = this.config.min_roll_off
      this.roll_off_max = this.config.max_roll_off
      this.sr_roll_min = this.config.min_sr_roll
      this.sr_roll_max = this.config.max_sr_roll
      this.qty_min = this.config.min_qty
      this.qty_max = this.config.max_qty
      //
      this.char_min = this.config.min_default_char
      this.char_max = this.config.max_default_char
      this.char_default_val = this.config.val_default_char
      // get races
      this.races = this.getRaces()
      // get race names
      this.race_names = extractNames(this.races)
      // get roles
      this.roles = this.getRoles()
      // get role labels
      this.role_labels = extractLabels(this.roles)
      // get player names
      this.player_names = this.getPlayerNames()
      // get default race
      this.defaultRace = this.races.find(
        (r) => r.name == this.config.default_race,
      )
      this.damage_mods = this.config.damage_mods
    },
    //========================================================
    // zero SR values
    //----------------
    zeroSRValues() {
      if (this.local_combatant.status == 'Inactive') {
        this.setZeroSRValues()
      }
    },
    //========================================================
    // set zero SR values
    //--------------------
    setZeroSRValues() {
      this.local_combatant.sr_roll = 0
      this.local_combatant.sr_adj = 0
      this.local_combatant.roll_off = 0
      this.local_combatant.seq = 0
      this.seq = ''
    },
    //========================================================
    // clear sub-status (on change of status)
    // clear srroll + adj if inactive
    //----------------------------------------
    clearSubStatus() {
      this.local_combatant.sub_status = ''
    },
    //========================================================
    // clear specific input error
    //----------------------------
    clearInputError(field) {
      if (this.errorFields.length > 0) {
        this.errorFields = this.errorFields.filter((entry) => entry !== field)
      }

      if (field === 'name') {
        const nameMessages = [
          this.getNameRequiredMessage(),
          this.getNameDuplicateMessage(),
        ]
        this.errors = this.errors.filter(
          (entry) => !nameMessages.includes(entry),
        )
      }
    },
    getNameRequiredMessage() {
      return window.bbUiErrorMsg('Name required')
    },
    getNameDuplicateMessage() {
      return window.bbUiErrorMsg('Name already exists')
    },
    addFieldError(field, message) {
      if (!this.errors.includes(message)) {
        this.errors.push(message)
      }

      if (!this.errorFields.includes(field)) {
        this.errorFields.push(field)
      }
    },
    validateName() {
      const trimmedName = String(this.local_combatant.name ?? '').trim()

      this.clearInputError('name')

      if (trimmedName) {
        return true
      }

      this.addFieldError('name', this.getNameRequiredMessage())
      return false
    },
    //========================================================
    // load combatant
    //----------------
    loadCombatant(existing_combatant) {
      this.isHydratingCombatant = true

      if (existing_combatant.seq == 0) {
        this.seq = ''
      } else {
        this.seq = existing_combatant.seq
      }

      // Seed watcher baselines before hydrating local state so the
      // initial load is not treated as a user-driven race/role change.
      this.original_race = existing_combatant.race_name
      this.original_role = existing_combatant.role
      this.original_status = existing_combatant.status

      this.local_combatant = loadCombatantValues(existing_combatant)

      // check if apply toggles
      this.chkToggleClick('second_weapon', existing_combatant.second_weapon)
      this.chkToggleClick('shield', existing_combatant.shield)
      this.chkToggleClick('ranged', existing_combatant.ranged)
      this.chkToggleClick('magic', existing_combatant.magic)
      this.chkToggleClick('special_action', existing_combatant.special_action)
      this.chkToggleClick(
        'special_reaction',
        existing_combatant.special_reaction,
      )

      this.$nextTick(() => {
        this.isHydratingCombatant = false
      })
    },
    //========================================================
    // set combatant
    //---------------
    async setCombatant(default_race) {
      //
      this.seq = ''
      this.local_combatant = setCombatantValues(default_race)
      this.original_race = this.local_combatant.race
      this.original_role = this.local_combatant.role
      //--------------------------------------
      // get race image url (default = human)
      //--------------------------------------
      // let raceDetails = getRaceDetails(this.local_combatant.race, this.races);
      // if (raceDetails) {
      this.local_combatant.image = default_race.image
      this.defaultRaceImage = this.local_combatant.image
      //---------------
      // preload image
      //---------------
      this.preloadImage(this.local_combatant.image)
      // }
    },
    //========================================================
    // preloadImage
    //-----------
    preloadImage(image) {
      let image_name = getStringAfterLastSlash(image)
      //-----------------------------
      // get blob from src url fetch
      //-----------------------------
      fetch(image)
        .then((response) => response.blob())
        .then((blob) => {
          //--------------------
          // create upload file
          //--------------------
          const uploadFile = async (file) => {
            const formData = new FormData()
            formData.append('image', file, image_name)
            this.local_combatant.image_file = formData
          }
          //-----------------------
          // trigger file creation
          //-----------------------
          uploadFile(blob)
        })
    },
    //========================================================
    // on submit
    //-----------
    onSubmit() {
      if (this.submitCommitted) {
        return
      }

      this.errors = []
      this.errorFields = []
      //------------------
      // where not delete
      //------------------
      if (!this.delete) {
        const hasName = this.validateName()
        if (!hasName) {
          this.$nextTick(() => {
            this.$refs.input?.focus?.()
          })
          return
        }

        //-----------------------------------------------
        // check name not exist (if troop where qty = 1)
        //-----------------------------------------------
        this.local_combatant.name = capitaliseMultipleWords(
          this.local_combatant.name,
        )
        if (
          (this.qty == 0 || this.qty == 1) &&
          this.local_combatant.name != this.original_name
        ) {
          if (isName(this.local_combatant.name, this.combatantNames)) {
            this.addFieldError('name', this.getNameDuplicateMessage())
          }
        }
        //-----------------------
        // continue if no errors
        //-----------------------
        if (this.errors.length == 0) {
          this.submitCommitted = true

          if (this.combatant != null) {
            this.updateCombatant()
          } else {
            this.addCombatants()
          }
        }
      }
    },
    //========================================================
    // add combatants
    //----------------
    async addCombatants() {
      //-------------------
      // go add combatants
      //-------------------
      this.emitter.emit('add-combatants', {
        new_combatant: this.local_combatant,
        qty: this.qty,
      })
      this.emitter.emit('close-combatant-side-panel', false)
      // }
    },
    //========================================================
    // update combatant
    //------------------
    updateCombatant() {
      this.emitter.emit('close-combatant-side-panel', false)
      //-----------------
      // set update parm
      //-----------------
      let update_parm = {
        payload: this.local_combatant,
        originalPayload: { ...this.combatant },
        forceRefilterCheck: false,
      }
      //--------------------------------------
      // set force refilter is status changed
      //--------------------------------------
      if (this.original_status != this.local_combatant.status) {
        update_parm.forceRefilterCheck = true
      }
      //-------------
      // call update
      //-------------
      this.emitter.emit('update-combatant', update_parm)
    },
    //=====================================================================
    // confirm deletion
    //------------------
    async confirmDeletion() {
      this.delete = true
      const ok = await this.$refs.confirmDialog.show({
        title: 'Delete Combatant',
        message: 'Confirm Deletion',
        subject: this.local_combatant.name,
        okButton: 'OK',
        cancelButton: 'Cancel',
        zIndex: 20000,
      })
      if (ok) {
        this.emitter.emit('close-combatant-side-panel', false)
        this.emitter.emit('delete-combatant', this.local_combatant.id)
      } else {
        this.delete = false
      }
    },
    //========================================================
    // next combatant
    //----------------
    nextCombatant() {
      this.emitter.emit('next-combatant', this.local_combatant)
    },
    //========================================================
    // previous combatant
    //--------------------
    prevCombatant() {
      this.emitter.emit('previous-combatant', this.local_combatant)
    },
    //========================================================
    // on key down
    //-------------
    onKeyDown(e) {
      if (!isAlphanumeric(e)) e.preventDefault()
    },
    formatRoleOption(roleOption) {
      if (roleOption && typeof roleOption === 'object') {
        return roleOption.label || roleOption.name || ''
      }

      return String(roleOption ?? '')
    },
    resolveRoleDetails(roleOption) {
      const roleValue = this.formatRoleOption(roleOption)
      return getRoleDetails(roleValue, this.roles)
    },
    showRolePreview(roleOption) {
      if (!roleOption) {
        this.hideRolePreview()
        return
      }

      const roleDetails = this.resolveRoleDetails(roleOption)

      if (!roleDetails) {
        this.hideRolePreview()
        return
      }

      this.rolePreviewName =
        roleDetails.label ||
        roleDetails.name ||
        this.formatRoleOption(roleOption)
      this.rolePreviewDetails = roleDetails
      this.rolePreviewVisible = true
    },
    hideRolePreview() {
      this.rolePreviewVisible = false
      this.rolePreviewName = ''
      this.rolePreviewDetails = null
    },
    formatSignedValue(value) {
      const numericValue = Number(value ?? 0)

      if (!Number.isFinite(numericValue) || numericValue === 0) {
        return '0'
      }

      return numericValue > 0 ? `+${numericValue}` : `${numericValue}`
    },
    getActiveRoleOptionNames(roleDetails) {
      if (!roleDetails) {
        return []
      }

      return [
        'shield',
        'second_weapon',
        'ranged',
        'magic',
        'special_action',
        'special_reaction',
      ].filter((optionName) => roleDetails[optionName] === true)
    },
    getOptionImageSrc(optionName) {
      return getOptionImage(optionName)
    },
    //========================================================
    // check toggles
    //---------------
    chkToggleClick(id, this_value) {
      switch (id) {
        case 'second_weapon':
          if (this.local_combatant.second_weapon != this_value) {
            this.onToggleClick(id, true)
          }
          break
        case 'shield':
          if (this.local_combatant.shield != this_value) {
            this.onToggleClick(id, true)
          }
          break
        case 'ranged':
          if (this.local_combatant.ranged != this_value) {
            this.onToggleClick(id, true)
          }
          break
        case 'magic':
          if (this.local_combatant.magic != this_value) {
            this.onToggleClick(id, true)
          }
          break
        case 'special_action':
          if (this.local_combatant.special_action != this_value) {
            this.onToggleClick(id, true)
          }
          break
        case 'special_reaction':
          if (this.local_combatant.special_reaction != this_value) {
            this.onToggleClick(id, true)
          }
          break
      }
    },
    //========================================================
    // toggle clicker (sliders)
    //-------------------------
    onToggleClick(id, auto) {
      // only auto-flip co-req slider if selected manually
      switch (id) {
        case 'second_weapon':
          this.local_combatant.second_weapon =
            !this.local_combatant.second_weapon
          if (!auto) {
            if (this.local_combatant.shield) {
              this.local_combatant.shield = !this.local_combatant.shield
            }
          }
          break
        case 'shield':
          this.local_combatant.shield = !this.local_combatant.shield
          if (!auto) {
            if (this.local_combatant.second_weapon) {
              this.local_combatant.second_weapon =
                !this.local_combatant.second_weapon
            }
          }
          break
        case 'ranged':
          this.local_combatant.ranged = !this.local_combatant.ranged
          break
        case 'magic':
          this.local_combatant.magic = !this.local_combatant.magic
          break
        case 'special_action':
          this.local_combatant.special_action =
            !this.local_combatant.special_action
          break
        case 'special_reaction':
          this.local_combatant.special_reaction =
            !this.local_combatant.special_reaction
          break
      }
    },
    //========================================================
    // close side panel
    //------------------
    closeSidePanel() {
      this.emitter.emit('close-combatant-side-panel', false)
    },
    //========================================================
    // force initial focus
    //---------------------
    forceInitialFocus() {
      if (!this.isCharacter) {
        this.editingMode = true
        this.$nextTick(() => {
          this.$refs.input.focus()
        })
      }
    },
    //========================================================
    // change CR
    //-----------
    changeCR(toggle) {
      this.local_combatant.cr = calcCombatAdj({
        reverse: !toggle,
        current_value: this.local_combatant.cr,
        min: this.config.min_cr,
        max: this.config.max_cr,
      })
    },
    //========================================================
    // capitalise after special char
    //-------------------------------
    capitaliseAfterSpecialChar() {
      //--------------------------
      // set name as target field
      //--------------------------
      let el = document.getElementById('name')
      //------------------------
      // listen to name keydown
      //------------------------
      el.addEventListener('keydown', function (e) {
        //--------------------
        // ignore command key
        //--------------------
        if (
          e.key !== 'Delete' &&
          e.key !== 'Backspace' &&
          !e.shiftKey &&
          !e.ctrlKey &&
          !e.option &&
          !e.altKey
        ) {
          let fieldVal = String(e.target.value)
          //---------------------
          // check previous char
          //---------------------
          let prevChar = fieldVal.charAt(e.target.value.length - 1)
          //------------------------------------
          // uc this char if prev = apos/hyphen
          //------------------------------------
          if (prevChar == "'" || prevChar == '-') {
            let key = e.key.toUpperCase()
            e.target.value = e.target.value + key
            e.preventDefault()
          }
        }
      })
    },
  },
  //========================================================
  // watches
  //---------
  watch: {
    watch_race() {
      this.onRaceChange()
    },
    watch_role() {
      this.onRoleChange()
    },
    watch_shield() {
      if (!this.isHydratingCombatant) {
        this.changeCR(this.local_combatant.shield)
      }
    },
    watch_second_weapon() {
      if (!this.isHydratingCombatant) {
        this.changeCR(this.local_combatant.second_weapon)
      }
    },
  },
  //========================================================
  // mounted
  //----------
  mounted() {
    //-----------------------------------
    // set capitalise after special char
    //-----------------------------------
    this.capitaliseAfterSpecialChar()
    //-----------------------------
    // set image not auto selected
    //-----------------------------
    this.image_selected = false
    //---------------------------
    // combatant image selection
    //---------------------------
    var fileInput = document.getElementById('selectImage')
    //-------------------
    // display new image
    //-------------------
    const preview = async (event) => {
      this.image_selected = true
      const file = event.target.files[0]
      const url = await readURL(file)
      this.local_combatant.image = url
      // create image file for uploading
      const formData = new FormData()
      formData.append('image', file)
      this.local_combatant.image_file = formData
    }
    //------------------------------
    // convert file to a base64 url
    //------------------------------
    const readURL = (file) => {
      return new Promise((res, rej) => {
        const reader = new FileReader()
        reader.onload = (e) => res(e.target.result)
        reader.onerror = (e) => rej(e)
        reader.readAsDataURL(file)
      })
    }
    //-----------------
    // image selection
    //-----------------
    fileInput.addEventListener('change', preview)
    //---------------------
    // get dropdown values
    //---------------------
    this.getDropDownValues()
    //---------------------------
    // save name for unique check
    //---------------------------
    if (this.combatant) this.original_name = this.combatant.name
    //----------------------------------------
    // load combatant emitter (for next/prev)
    //----------------------------------------
    this.emitter.on('load-combatant', (combatant) => {
      this.loadCombatant(combatant)
      this.original_combatant = copyObjectNoRef(this.local_combatant)
    })
    //---------------
    // counter emits
    //---------------
    this.emitter.on('counter_ca', (return_count) => {
      this.local_combatant.ca = return_count
    })
    this.emitter.on('counter_cr', (return_count) => {
      this.local_combatant.cr = return_count
    })
    this.emitter.on('counter_dex', (return_count) => {
      this.local_combatant.dex = return_count
    })
    this.emitter.on('counter_sr', (return_count) => {
      this.local_combatant.sr = return_count
    })
    this.emitter.on('counter_sr_roll', (return_count) => {
      this.local_combatant.sr_roll = return_count
    })
    this.emitter.on('counter_roll_off', (return_count) => {
      this.local_combatant.roll_off = return_count
    })
    this.emitter.on('counter_sr_adj', (return_count) => {
      this.local_combatant.sr_adj = return_count
    })
    this.emitter.on('counter_qty', (return_count) => {
      this.qty = return_count
    })
    //-----------------------
    // load combatant values
    //-----------------------
    if (this.combatant != null) {
      this.loadCombatant(this.combatant)
      this.new_combatant = false
    } else {
      this.setCombatant(this.defaultRace)
      this.new_combatant = true
    }
    //----------------------
    // save combatant values
    //-----------------------
    this.original_combatant = copyObjectNoRef(this.local_combatant)
    console.log(
      'CombatantCard mounted: original_combatant',
      this.original_combatant,
    )
    //------------------------
    // set initial input focus
    //------------------------
    this.forceInitialFocus()
  },
}
</script>

<style scoped>
.bb-role-preview-anchor {
  position: relative;
}

:deep(.bb-visibility-select .vs__dropdown-toggle) {
  min-height: 58px;
  align-items: center;
}

:deep(.bb-visibility-select .vs__selected-options) {
  flex-wrap: nowrap;
  align-items: center;
  max-height: none;
  overflow: visible;
}

:deep(.bb-visibility-select .vs__selected),
:deep(.bb-visibility-select .vs__search),
:deep(.bb-visibility-select .vs__search::placeholder) {
  margin: 0;
  line-height: 1.5;
  text-align: left;
  text-transform: none;
}

.bb-role-select-value,
.bb-role-option {
  display: block;
  width: 100%;
}

.bb-role-preview-panel {
  position: fixed;
  top: 2rem;
  left: calc(44vw + 8rem);
  right: auto;
  transform: translate(10rem, 4rem) scale(1.2);
  transform-origin: top left;
  z-index: 6200;
  width: calc((56vw - 8rem) * 1.02);
  min-width: 36rem;
  max-width: 58rem;
  min-height: 12rem;
  padding: 0.8rem 0.95rem;
  border: none;
  border-radius: 0.5rem;
  color: #ffffff !important;
  background: #323234;
  box-shadow: 0 0.9rem 1.8rem rgba(0, 0, 0, 0.45);
  pointer-events: none;
}

.bb-role-preview-panel,
.bb-role-preview-panel * {
  color: #ffffff !important;
}

.bb-role-preview-table {
  width: 100%;
  table-layout: fixed;
  font-size: 1.2rem;
  vertical-align: middle;
  color: #ffffff !important;
}

.bb-role-preview-table thead th {
  border-bottom-color: #4a4d54;
  color: #ffffff !important;
  font-size: 1.15rem;
  font-weight: normal;
}

.bb-role-preview-table th,
.bb-role-preview-table td {
  padding: 0.65rem 0.55rem;
  vertical-align: middle;
  background: transparent;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-color: #4a4d54;
}

.bb-role-preview-heading,
.bb-role-preview-value {
  text-align: center;
}

.bb-role-preview-table th:nth-child(1),
.bb-role-preview-table td:nth-child(1) {
  width: 5.5rem;
}

.bb-role-preview-table th:nth-child(2),
.bb-role-preview-table td:nth-child(2) {
  width: 8rem;
}

.bb-role-preview-table th:nth-child(3),
.bb-role-preview-table td:nth-child(3),
.bb-role-preview-table th:nth-child(4),
.bb-role-preview-table td:nth-child(4),
.bb-role-preview-table th:nth-child(5),
.bb-role-preview-table td:nth-child(5) {
  width: 4.25rem;
}

.bb-role-preview-table th:nth-child(6) {
  width: 11rem;
  text-align: center;
  padding-left: 0.8rem;
  padding-right: 0.2rem;
}

.bb-role-preview-table td:nth-child(6) {
  width: 11rem;
  padding-left: 0.8rem;
  padding-right: 0.2rem;
}

.bb-role-preview-image-cell {
  width: 5rem;
  height: 5rem;
}

.bb-role-preview-table td:last-child {
  min-width: 10rem;
}

.bb-role-preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border: none;
  background: transparent;
}

.bb-role-preview-options {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 2.5rem;
  transform: translateX(0.35rem);
}

.bb-role-preview-option {
  width: 2.8rem;
  height: 2.8rem;
  object-fit: cover;
}
</style>
